import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faDownload } from '@fortawesome/free-solid-svg-icons';
import axios from '../utils/api';
import { toast } from 'react-toastify';
import { API_BASE } from '../utils/env';

const PdfManagement = ({ onBack }) => {
  const [pdfs, setPdfs] = useState([]);
  const [qrCodeStats, setQrCodeStats] = useState({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfForm, setPdfForm] = useState({
    name: '',
    description: '',
    privacy: 'Public',
    allowDownload: true,
    allowShare: true,
    backgroundColor: '#FFFFFF',
    backgroundImage: ''
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [loading, setLoading] = useState(true);

  // QR Code modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [qrCodePdfId, setQrCodePdfId] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);

  useEffect(() => {
    loadPdfs();
  }, []);

  const loadPdfs = async () => {
    try {
      const response = await axios.get('/api/pdf/admin/all');
      setPdfs(response.data);
      
      // Buscar estatísticas de QR code para cada PDF
      const statsPromises = response.data.map(pdf => 
        axios.get(`/api/qrcode/${pdf.id}/stats`)
          .then(res => ({ pdfId: pdf.id, stats: res.data }))
          .catch(() => ({ pdfId: pdf.id, stats: null }))
      );
      
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(result => {
        if (result.stats) {
          statsMap[result.pdfId] = result.stats;
        }
      });
      
      setQrCodeStats(statsMap);
    } catch (error) {
      toast.error('Erro ao carregar PDFs');
      console.error('Error loading PDFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePdf = async (pdfId) => {
    if (window.confirm('Tem certeza que deseja excluir este PDF?')) {
      try {
        await axios.delete(`/api/pdf/admin/${pdfId}`);
        setPdfs((prev) => prev.filter((p) => p.id !== pdfId));
        setQrCodeStats((prev) => {
          const next = { ...prev };
          delete next[pdfId];
          return next;
        });
        toast.success('PDF excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir PDF:', error);
        toast.error('Erro ao excluir PDF');
      }
    }
  };

  const handleDownloadQRCode = async (pdfId) => {
    try {
      setQrCodeLoading(true);
      setQrCodePdfId(pdfId);
      setShowQRModal(true);
      
      // Primeiro, gera o QR code
      await axios.post(`/api/qrcode/${pdfId}/generate`);
      
      // Depois, obtém a imagem do QR code
      const imageUrl = `${API_BASE}/api/qrcode/${pdfId}/image`;
      setQrCodeImage(imageUrl);
      
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
      toast.error('Erro ao gerar QR code');
      setShowQRModal(false);
    } finally {
      setQrCodeLoading(false);
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrCodeImage('');
    setQrCodePdfId(null);
    setQrCodeLoading(false);
  };

  const downloadQRCode = () => {
    if (qrCodePdfId) {
      window.open(`${API_BASE}/api/qrcode/${qrCodePdfId}/download`, '_blank');
    }
  };

  const handleUpdatePdf = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', pdfForm.name);
      formData.append('description', pdfForm.description);
      formData.append('privacy', pdfForm.privacy);
      formData.append('allowDownload', pdfForm.allowDownload);
      formData.append('allowShare', pdfForm.allowShare);
      formData.append('backgroundColor', pdfForm.backgroundColor);
      
      // Handle background image upload
      if (backgroundImageFile) {
        formData.append('backgroundFile', backgroundImageFile);
      } else {
        formData.append('backgroundImage', pdfForm.backgroundImage || '');
      }
      
      if (pdfFile) {
        formData.append('file', pdfFile);
      }

      await axios.put(`/api/pdf/admin/${selectedPdf.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('PDF atualizado com sucesso!');
      setShowPdfModal(false);
      setPdfForm({
        name: '',
        description: '',
        privacy: 'Public',
        allowDownload: true,
        allowShare: true,
        backgroundColor: '#FFFFFF',
        backgroundImage: ''
      });
      setPdfFile(null);
      setBackgroundImageFile(null);
      setSelectedPdf(null);
      loadPdfs();
    } catch (error) {
      toast.error('Erro ao atualizar PDF');
    }
  };

  const openPdfModal = (pdf) => {
    setSelectedPdf(pdf);
    setPdfForm({
      name: pdf.name || '',
      description: pdf.description || '',
      privacy: pdf.privacy || 'Public',
      allowDownload: pdf.allowDownload || false,
      allowShare: pdf.allowShare || false,
      backgroundColor: pdf.backgroundColor || '#FFFFFF',
      backgroundImage: pdf.backgroundImage || ''
    });
    setPdfFile(null);
    setBackgroundImageFile(null);
    setShowPdfModal(true);
  };

  const viewPdf = (pdf) => {
    window.open(`/viewer/${pdf.id}`, '_blank');
  };

  const getPrivacyBadge = (privacy) => {
    return privacy === 'Public' ? 
      <span style={{
        backgroundColor: '#10b981',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.375rem',
        fontSize: '0.75rem',
        fontWeight: '500'
      }}>Público</span> : 
      <span style={{
        backgroundColor: '#f59e0b',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.375rem',
        fontSize: '0.75rem',
        fontWeight: '500'
      }}>Privado</span>;
  };

  const getFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const styles = {
    container: {
      backgroundColor: '#f1f5f9',
      minHeight: '100vh',
      padding: '1rem'
    },
    card: {
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    cardHeader: {
      backgroundColor: '#00B2A9',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      padding: '1.5rem'
    },
    cardBody: {
      backgroundColor: 'white',
      color: '#64748b'
    },
    primaryButton: {
      backgroundColor: '#00B2A9',
      borderColor: '#00B2A9',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#002F6C',
      borderColor: '#002F6C',
      color: 'white'
    },
    lightButton: {
      backgroundColor: 'white',
      borderColor: '#00B2A9',
      color: '#00B2A9'
    },
    infoButton: {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      color: 'white'
    },
    warningButton: {
      backgroundColor: '#f59e0b',
      borderColor: '#f59e0b',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      borderColor: '#ef4444',
      color: 'white'
    },
    outlineButton: {
      backgroundColor: 'transparent',
      borderColor: '#00B2A9',
      color: '#00B2A9'
    },
    table: {
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    tableHeader: {
      backgroundColor: '#f8fafc',
      color: '#002F6C',
      fontWeight: '600'
    },
    textMuted: {
      color: '#94a3b8'
    },
    textPrimary: {
      color: '#002F6C'
    },
    spinner: {
      color: '#00B2A9'
    },
    modal: {
      color: '#64748b'
    },
    modalHeader: {
      backgroundColor: '#00B2A9',
      color: 'white',
      borderRadius: '8px 8px 0 0'
    },
    alert: {
      backgroundColor: '#e0f2fe',
      borderColor: '#00B2A9',
      color: '#002F6C'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Container className="py-5">
          <div className="text-center">
            <div className="spinner-border" role="status" style={styles.spinner}>
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          .btn-light-custom:hover {
            background-color: #00B2A9 !important;
            color: white !important;
            border-color: #00B2A9 !important;
          }
          .btn-outline-custom:hover {
            background-color: #00B2A9 !important;
            color: white !important;
          }
          .btn-group-vertical .btn {
            margin-bottom: 0.25rem !important;
          }
          .btn-group-vertical .btn:last-child {
            margin-bottom: 0 !important;
          }
        `}
      </style>
      <Container fluid className="py-4">
        <Row>
          <Col>
            <Card style={styles.card}>
              <Card.Header style={styles.cardHeader} className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">Gestão de Revistas Digitais</h3>
                  <small>Gerenciar todas as revistas digitais do sistema</small>
                </div>
                <Button 
                  style={{
                    ...styles.lightButton,
                    transition: 'all 0.2s ease'
                  }}
                  size="sm" 
                  onClick={onBack}
                  className="btn-light-custom"
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Voltar ao Painel
                </Button>
              </Card.Header>
              <Card.Body style={styles.cardBody}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 style={styles.textPrimary}>PDFs Cadastrados</h4>
                    <p style={styles.textMuted} className="mb-0">Total: {pdfs.length} PDFs</p>
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      style={{
                        ...styles.outlineButton,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={loadPdfs}
                      className="btn-outline-custom"
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Atualizar
                    </Button>
                  </div>
                </div>
                
                <div style={styles.table}>
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr style={styles.tableHeader}>
                        <th>ID</th>
                        <th>Nome do PDF</th>
                        <th>Usuário</th>
                        <th>Privacidade</th>
                        <th>Data de Upload</th>
                        <th>Cliques QR</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pdfs.map(pdf => (
                        <tr key={pdf.id}>
                          <td>{pdf.id}</td>
                          <td>
                            <div>
                              <strong>{pdf.name}</strong>
                              {pdf.description && (
                                <div style={styles.textMuted} className="small">{pdf.description}</div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{pdf.user?.firstName} {pdf.user?.lastName}</strong>
                              <div style={styles.textMuted} className="small">{pdf.user?.email}</div>
                            </div>
                          </td>
                          <td>{getPrivacyBadge(pdf.privacy)}</td>
                          <td>{new Date(pdf.uploadDate).toLocaleDateString('pt-BR')}</td>
                          <td>
                            {qrCodeStats[pdf.id] ? (
                              <Badge bg="info" pill className="px-3 py-2">
                                {qrCodeStats[pdf.id].clickCount || 0}
                              </Badge>
                            ) : (
                              <Badge bg="secondary" pill className="px-3 py-2">0</Badge>
                            )}
                          </td>
                          <td>
                            <div className="btn-group-vertical btn-group-sm" role="group">
                              <Button 
                                style={styles.infoButton}
                                size="sm" 
                                className="mb-1"
                                onClick={() => viewPdf(pdf)}
                              >
                                <i className="bi bi-eye me-1"></i>
                                Visualizar
                              </Button>
                              <Button 
                                style={styles.primaryButton}
                                size="sm" 
                                className="mb-1"
                                onClick={() => window.open(`/api/pdf/${pdf.id}/file`, '_blank')}
                              >
                                <i className="bi bi-download me-1"></i>
                                Download
                              </Button>
                              <Button 
                                style={styles.warningButton}
                                size="sm" 
                                className="mb-1"
                                onClick={() => openPdfModal(pdf)}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                Editar
                              </Button>
                              <Button 
                                style={styles.dangerButton}
                                size="sm"
                                className="mb-1"
                                onClick={() => handleDeletePdf(pdf.id)}
                              >
                                <i className="bi bi-trash me-1"></i>
                                Excluir
                              </Button>
                              <Button 
                                style={{
                                  backgroundColor: '#6366f1',
                                  borderColor: '#6366f1',
                                  color: 'white'
                                }}
                                size="sm"
                                onClick={() => handleDownloadQRCode(pdf.id)}
                              >
                                <i className="bi bi-qr-code me-1"></i>
                                QR Code
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {pdfs.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-file-earmark-pdf" style={{fontSize: '4rem', color: '#94a3b8'}}></i>
                    <h5 className="mt-3" style={styles.textMuted}>Nenhum PDF encontrado</h5>
                    <p style={styles.textMuted}>Os PDFs enviados pelos usuários aparecerão aqui</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal para alterar PDF */}
        <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} size="lg">
          <Modal.Header closeButton style={styles.modalHeader}>
            <Modal.Title>Editar PDF</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdatePdf}>
            <Modal.Body style={styles.modal}>
              <Alert style={styles.alert}>
                Editando PDF: <strong>{selectedPdf?.name}</strong>
              </Alert>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Nome</Form.Label>
                    <Form.Control
                      type="text"
                      value={pdfForm.name}
                      onChange={(e) => setPdfForm({...pdfForm, name: e.target.value})}
                      required
                      style={{borderColor: '#d1d5db', color: '#64748b'}}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Privacidade</Form.Label>
                    <Form.Select
                      value={pdfForm.privacy}
                      onChange={(e) => setPdfForm({...pdfForm, privacy: e.target.value})}
                      style={{borderColor: '#d1d5db', color: '#64748b'}}
                    >
                      <option value="Public">Público</option>
                      <option value="Private">Privado</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={pdfForm.description}
                  onChange={(e) => setPdfForm({...pdfForm, description: e.target.value})}
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir Download"
                      checked={pdfForm.allowDownload}
                      onChange={(e) => setPdfForm({...pdfForm, allowDownload: e.target.checked})}
                      style={{color: '#002F6C'}}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir Compartilhamento"
                      checked={pdfForm.allowShare}
                      onChange={(e) => setPdfForm({...pdfForm, allowShare: e.target.checked})}
                      style={{color: '#002F6C'}}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Cor de Fundo</Form.Label>
                    <Form.Control
                      type="color"
                      value={pdfForm.backgroundColor}
                      onChange={(e) => setPdfForm({...pdfForm, backgroundColor: e.target.value})}
                      style={{borderColor: '#d1d5db'}}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Imagem de Fundo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => setBackgroundImageFile(e.target.files[0])}
                      style={{borderColor: '#d1d5db', color: '#64748b'}}
                    />
                    <Form.Text style={styles.textMuted}>
                      Formatos aceitos: JPG, PNG, GIF. Deixe em branco para manter a atual.
                    </Form.Text>
                    {pdfForm.backgroundImage && !backgroundImageFile && (
                      <div className="mt-2">
                        <small style={{color: '#10b981'}}>
                          <i className="bi bi-check-circle me-1"></i>
                          Imagem de fundo atual: {pdfForm.backgroundImage.split('/').pop()}
                        </small>
                        <Button
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: '#ef4444',
                            color: '#ef4444',
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem'
                          }}
                          size="sm"
                          className="ms-2"
                          onClick={() => setPdfForm({...pdfForm, backgroundImage: ''})}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Remover
                        </Button>
                      </div>
                    )}
                    {backgroundImageFile && (
                      <div className="mt-2">
                        <small style={{color: '#3b82f6'}}>
                          <i className="bi bi-upload me-1"></i>
                          Nova imagem selecionada: {backgroundImageFile.name}
                        </small>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Substituir Arquivo PDF (opcional)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
                <Form.Text style={styles.textMuted}>
                  Deixe em branco para manter o arquivo atual
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                style={{backgroundColor: '#94a3b8', borderColor: '#94a3b8'}}
                onClick={() => setShowPdfModal(false)}
              >
                Cancelar
              </Button>
              <Button 
                style={styles.primaryButton}
                type="submit"
              >
                Atualizar PDF
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* QR Code Modal */}
        <Modal 
          show={showQRModal} 
          onHide={closeQRModal}
          centered
          backdrop="static"
        >
          <div style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Modal.Header 
              closeButton 
              className="border-0 pb-2" 
              style={{ backgroundColor: '#ffffff' }}
            >
              <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <FontAwesomeIcon icon={faQrcode} className="me-2" />
              QR Code
            </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
              {qrCodeLoading ? (
                <div className="d-flex flex-column align-items-center py-4">
                  <Spinner animation="border" style={{ color: '#00B2A9' }} />
                  <p className="mt-3 mb-0" style={{ color: '#002F6C' }}>Gerando QR Code...</p>
                </div>
              ) : (
                qrCodeImage && (
                  <div className="d-flex flex-column align-items-center">
                    <img 
                      src={qrCodeImage} 
                      alt="QR Code" 
                      style={{ 
                        maxWidth: '250px', 
                        maxHeight: '250px',
                        border: '2px solid #f1f5f9',
                        borderRadius: '8px',
                        padding: '10px',
                        backgroundColor: '#ffffff'
                      }} 
                    />
                    <p className="mt-3 mb-0 small" style={{ color: '#666' }}>
                      Escaneie o QR Code para acessar o documento
                    </p>
                  </div>
                )
              )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
              <Button 
                variant="outline-secondary" 
                onClick={closeQRModal}
                style={{ borderRadius: '8px' }}
              >
                Fechar
              </Button>
              {!qrCodeLoading && qrCodeImage && (
                <Button
                  onClick={downloadQRCode}
                  style={{
                    backgroundColor: '#00B2A9',
                    borderColor: '#00B2A9',
                    borderRadius: '8px'
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" /> Baixar QR Code
                </Button>
              )}
            </Modal.Footer>
          </div>
        </Modal>
      </Container>
    </div>
  );
};

export default PdfManagement;